/**
 * BR-U6-5準拠のエラー分類・表示ロジック。
 * NFR Design 1.1: 自動リトライは行わず、手動再試行導線を提供する。
 */

import type { ClassifiedError, FailureKind } from "../types";
import type { RelayResponse } from "./relayClient";

/**
 * 中継エンドポイントのレスポンスを業務エラー/システムエラー/認証エラーに分類する（BR-U6-5）。
 */
export function classifyError<T>(response: RelayResponse<T>): ClassifiedError {
  if (response.networkError) {
    return { kind: "SYSTEM", message: "時間をおいて再度お試しください。" };
  }

  if (response.status === 401) {
    return { kind: "AUTH", message: "ログインが必要です。" };
  }

  if (response.status >= 500) {
    return { kind: "SYSTEM", message: "時間をおいて再度お試しください。" };
  }

  if (response.status >= 400 && response.businessErrorCode) {
    return {
      kind: "BUSINESS",
      businessErrorCode: response.businessErrorCode,
      message: businessErrorMessage(response.businessErrorCode),
    };
  }

  // 業務エラーコードが判別できない4xxはシステムエラー扱い（BR-U6-5）
  return { kind: "SYSTEM", message: "時間をおいて再度お試しください。" };
}

/**
 * 効果分析ログの失敗大分類（failureKind）を ClassifiedError から導出する（BR-AN-6）。
 *
 * <p>`APPOINTMENT_SUBMIT_FAILED` イベントに載せる原因分類。business-logic-model.md §3 の導出ルール:
 * BUSINESS かつ SLOT_NOT_AVAILABLE/SLOT_ALREADY_RESERVED → SLOT_UNAVAILABLE、
 * BUSINESS かつ DUPLICATE_COMPANY_APPOINTMENT → DUPLICATE、その他の BUSINESS → VALIDATION、
 * SYSTEM → SYSTEM、AUTH → AUTH。
 */
export function toFailureKind(classified: ClassifiedError): FailureKind {
  if (classified.kind === "SYSTEM") {
    return "SYSTEM";
  }
  if (classified.kind === "AUTH") {
    return "AUTH";
  }
  // BUSINESS
  switch (classified.businessErrorCode) {
    case "SLOT_NOT_AVAILABLE":
    case "SLOT_ALREADY_RESERVED":
      return "SLOT_UNAVAILABLE";
    case "DUPLICATE_COMPANY_APPOINTMENT":
      return "DUPLICATE";
    default:
      return "VALIDATION";
  }
}

/**
 * 業務エラーコードごとの表示メッセージ（BR-U6-5）。
 * コード値はspica-apiのErrorCode enum（SNAKE_CASE）に対応する。
 */
function businessErrorMessage(code: string): string {
  switch (code) {
    case "SLOT_NOT_AVAILABLE":
    case "SLOT_ALREADY_RESERVED":
      return "その枠は埋まりました。他の候補をお選びください。";
    case "CANCELLATION_DEADLINE_EXCEEDED":
      return "キャンセル期限を過ぎているため、こちらからのキャンセルはできません。出展企業へお問い合わせください。";
    default:
      return "時間をおいて再度お試しください。";
  }
}

/**
 * コンソールログ出力（NFR-U6-SEC3: 識別子とエラー種別のみ、個人情報・APIレスポンス生データは出力しない）。
 */
export function logClientWarning(context: string, identifier?: string): void {
  // eslint-disable-next-line no-console
  console.warn(`[spica-widget] ${context}`, identifier ? { identifier } : undefined);
}
